/**
 * Indian-locale formatting helpers (₹, lakh/crore grouping).
 */

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

/** Format a price in Indian rupee grouping, e.g. 125000 → "₹1,25,000". */
export function formatINR(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return inr.format(value);
}

/** Plain grouped number without currency symbol, e.g. 125000 → "1,25,000". */
export function formatIndianNumber(value: number): string {
  return new Intl.NumberFormat('en-IN').format(value);
}

/** Percentage price drop (positive integer) or null when not a drop. */
export function priceDropPercent(
  oldPrice: number | null | undefined,
  newPrice: number | null | undefined,
): number | null {
  if (
    oldPrice === null ||
    oldPrice === undefined ||
    newPrice === null ||
    newPrice === undefined ||
    oldPrice <= 0 ||
    newPrice >= oldPrice
  ) {
    return null;
  }
  return Math.round(((oldPrice - newPrice) / oldPrice) * 100);
}

/** Relative "just now / 2m ago" style label. */
export function relativeTime(ts: number, now: number = Date.now()): string {
  const diff = Math.max(0, now - ts);
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

/** Basic E.164 India normalization: strips spaces, ensures +91 default. */
export function toE164India(raw: string): string | null {
  const trimmed = raw.replace(/[\s()-]/g, '');
  if (/^\+\d{8,15}$/.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
  return null;
}

/** True for a valid-looking E.164 number. */
export function isValidE164(value: string): boolean {
  return /^\+\d{8,15}$/.test(value);
}

/**
 * Format a raw sheet cell that is meant to be a price. Sheets hold plain
 * numbers like "124000"; dealers expect "₹1,24,000". Anything non-numeric
 * (e.g. "CALL", "*GST PAID*") is returned untouched.
 */
export function formatPriceCell(raw: string): string {
  const cleaned = raw.replace(/[₹,\s]/g, '');
  if (cleaned === '' || !/^\d+(\.\d+)?$/.test(cleaned)) return raw;
  const n = Number(cleaned);
  return Number.isFinite(n) ? formatINR(n) : raw;
}
